import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Activity, Cpu, Globe2, Zap } from 'lucide-react';

type Stats = {
  gpus_available: number;
  total_vram_gb: number;
  total_vram_tb: number;
  total_tflops: number;
  total_pflops: number;
  pool_types: number;
  regions: string[];
  last_updated: number;
  refresh_interval_seconds: number;
};

// Eased counter — animates from current display value to target over 800ms.
function useAnimatedNumber(target: number) {
  const [display, setDisplay] = useState(target);
  useEffect(() => {
    let raf = 0;
    const start = display;
    const startTs = performance.now();
    const duration = 800;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTs) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(start + (target - start) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);
  return display;
}

function formatInt(n: number): string {
  return n.toLocaleString('en-US');
}

type StatTile = {
  icon: React.ComponentType<{ className?: string }>;
  value: string;
  label: string;
  sub: string;
};

/**
 * Landing-page version of the sovereign compute capacity panel. Same
 * /api/training/compute-stats source as the dashboard widget, but bigger
 * typography and framed for first-time visitors — not signed-in users.
 * Never names the underlying providers.
 */
export default function SovereignComputeStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await fetch('/api/training/compute-stats');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as Stats;
        if (!cancelled) {
          setStats(j);
          setErrored(false);
        }
      } catch {
        if (!cancelled) setErrored(true);
      }
    };
    void tick();
    const interval = window.setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  const gpus = useAnimatedNumber(stats?.gpus_available ?? 0);
  // x10 trick so we can animate to one-decimal precision smoothly.
  const vramTbX10 = useAnimatedNumber(Math.round((stats?.total_vram_tb ?? 0) * 10));
  const pflopsX10 = useAnimatedNumber(Math.round((stats?.total_pflops ?? 0) * 10));

  const tiles: StatTile[] = [
    {
      icon: Cpu,
      value: formatInt(gpus),
      label: 'GPUs ready',
      sub: 'across our sovereign compute network',
    },
    {
      icon: Activity,
      value: `${(vramTbX10 / 10).toFixed(1)} TB`,
      label: 'Total VRAM',
      sub: 'pooled across our network',
    },
    {
      icon: Zap,
      value: `${(pflopsX10 / 10).toFixed(1)} PFLOPS`,
      label: 'Peak compute',
      sub: 'available right now',
    },
  ];

  return (
    <section id="sovereign-compute-network" className="px-4 py-16 md:py-20">
      <div className="container mx-auto max-w-6xl">
        <motion.div

          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="overflow-hidden rounded-3xl border border-primary/15 bg-gradient-to-br from-primary/[0.07] via-background to-background p-8 shadow-[0_30px_80px_-50px_rgba(220,38,38,0.5)] backdrop-blur md:p-12"
        >
          <div className="flex flex-col items-start gap-3 md:flex-row md:items-end md:justify-between md:gap-6">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1 text-xs font-medium uppercase tracking-[0.22em] text-primary">
                <Globe2 className="h-3.5 w-3.5" />
                Sovereign compute network
              </div>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight md:text-5xl">
                Real infrastructure. Available now.
              </h2>
              <p className="mt-3 max-w-xl text-base leading-7 text-muted-foreground md:text-lg">
                Live capacity from our pooled sovereign GPU network — refreshed every minute, ready for your training and inference workloads.
              </p>
            </div>
            {!errored && stats && (
              <div className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
                </span>
                Live · updates every {stats.refresh_interval_seconds}s
              </div>
            )}
          </div>

          <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
            {tiles.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.label}
                  className="rounded-2xl border border-border/60 bg-background/70 p-6 backdrop-blur"
                >
                  <div className="flex items-center gap-2 text-primary/90">
                    <Icon className="h-4 w-4" />
                    <span className="text-xs font-semibold uppercase tracking-[0.22em]">
                      {t.label}
                    </span>
                  </div>
                  <div className="mt-3 text-4xl font-semibold tracking-tight tabular-nums md:text-5xl">
                    {errored ? '—' : t.value}
                  </div>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">{t.sub}</p>
                </div>
              );
            })}
          </div>

          {stats && (
            <p className="mt-6 text-center text-xs text-muted-foreground md:text-left">
              {stats.pool_types} GPU pools · {stats.regions.join(' · ')}
            </p>
          )}
          {errored && (
            <p className="mt-6 text-center text-xs text-muted-foreground md:text-left">
              Network stats temporarily unavailable.
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
